/**
 * Track list under the timeline ruler: one row per animated thing (the
 * camera, then every element·effect track), keys as diamonds aligned to the
 * ruler, an add-track bar and a config row for the active effect's knobs.
 *
 * Compared with the fork's timeline: labels never clip (two lines, ellipsis,
 * full text in the tooltip), each row can be removed, the active row is
 * obvious, adding a track is one bar instead of a mode switch, and config
 * knobs carry their help as real tooltips.
 */

import { Button, ColorPicker, Container, Label, NumericInput, SelectInput } from '@playcanvas/pcui';

import type { Element } from '../../element';
import type { Events } from '../../events';
import type { Scene } from '../../scene';
import { i18n } from '../../ui/localization';
import type { Tooltips } from '../../ui/tooltips';
import type { ConfigParam, EffectDef } from '../effect-catalog';
import type { TrackTarget } from '../track-manager';
import type { TrackInfo } from '../tracks';

const PADDING = 20;   // the ruler's own inset (timeline-panel.ts Ticks)

type Row = {
    info: TrackInfo;
    row: Container;
    strip: HTMLElement;
    playhead: HTMLElement;
};

class TrackList extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'ollo-track-list'
        };
        super(args);

        // ── add-track bar ────────────────────────────────────────────────
        const addBar = new Container({ class: 'ollo-track-add' });
        const addLabel = new Label({ class: 'ollo-track-add-label' });
        i18n.bindText(addLabel, 'ollo.timeline.add-track');
        const elementPicker = new SelectInput({ class: 'ollo-track-select', options: [] });
        const effectPicker = new SelectInput({ class: 'ollo-track-select', options: [] });
        const addButton = new Button({ class: 'ollo-track-add-button', text: '' });
        tooltips.register(addButton, () => i18n.t('ollo.timeline.add-track.tooltip'), 'top');
        addBar.append(addLabel);
        addBar.append(elementPicker);
        addBar.append(effectPicker);
        addBar.append(addButton);

        const elementKey = (el: Element | null) => (el ? `${el.type}:${el.uid}` : 'camera');
        const nameOf = (el: Element) => (el as unknown as { name?: string }).name ?? `${el.type} ${el.uid}`;
        let targets: Element[] = [];

        const pickedElement = (): Element | null => targets.find(e => elementKey(e) === elementPicker.value) ?? null;

        const refreshEffectPicker = () => {
            const el = pickedElement();
            if (!el) {
                (effectPicker as any).options = [{ v: 'camera-pose', t: i18n.t('ollo.timeline.camera-pose') }];
                effectPicker.value = 'camera-pose';
                effectPicker.enabled = false;
                return;
            }
            effectPicker.enabled = true;
            const defs = (events.invoke('track.effectsFor', el) as EffectDef[]) ?? [];
            const opts = defs.map(d => ({ v: d.id, t: d.name }));
            const prev = effectPicker.value;
            (effectPicker as any).options = opts;
            effectPicker.value = opts.find(o => o.v === prev) ? prev : (opts[0]?.v ?? '');
        };

        const refreshElementPicker = () => {
            targets = (events.invoke('tracks.targets') as Element[]) ?? [];
            const opts = [
                { v: 'camera', t: i18n.t('ollo.timeline.camera') },
                ...targets.map(e => ({ v: elementKey(e), t: nameOf(e) }))
            ];
            const prev = elementPicker.value;
            (elementPicker as any).options = opts;
            elementPicker.value = opts.find(o => o.v === prev) ? prev : opts[0].v;
            refreshEffectPicker();
        };

        elementPicker.on('change', refreshEffectPicker);
        addButton.on('click', () => {
            const el = pickedElement();
            const effectId = el ? (effectPicker.value as string) : 'camera-pose';
            if (!effectId) return;
            events.invoke('tracks.ensure', { element: el, effectId });
            events.fire('track.setActive', { element: el, effectId });
            rebuildList();
        });

        // ── rows ─────────────────────────────────────────────────────────
        const rowsContainer = new Container({ class: 'ollo-track-rows' });
        const configRow = new Container({ class: 'ollo-track-config', hidden: true });
        this.append(addBar);
        this.append(rowsContainer);
        this.append(configRow);

        let rows: Row[] = [];

        const stripWidth = (): number => {
            const first = rows[0]?.strip;
            return Math.max(1, (first?.getBoundingClientRect().width ?? 0) - PADDING * 2);
        };
        const totalFrames = () => Math.max(1, events.invoke('timeline.frames') as number);
        const offsetFromFrame = (frame: number) => {
            const n = totalFrames();
            return PADDING + (n <= 1 ? 0 : Math.floor(frame / (n - 1) * stripWidth()));
        };
        const frameFromOffset = (offset: number) => {
            const n = totalFrames();
            return Math.max(0, Math.min(n - 1, Math.floor((offset - PADDING) / stripWidth() * (n - 1))));
        };

        const activeTarget = () => events.invoke('track.activeTarget') as TrackTarget;
        const isCameraTarget = (t: TrackTarget) => !t.effectId || t.effectId === 'camera-pose';
        const rowIsActive = (info: TrackInfo) => {
            const t = activeTarget();
            if (info.kind === 'camera') return isCameraTarget(t);
            return t.element === info.element && t.effectId === info.effectId;
        };
        const targetOf = (info: TrackInfo): TrackTarget => ({ element: info.element, effectId: info.effectId });

        const buildKeys = (r: Row) => {
            r.strip.querySelectorAll('.ollo-track-key').forEach(el => el.remove());
            const keys = r.info.track.keys;
            for (const keyFrame of keys) {
                const diamond = document.createElement('div');
                diamond.classList.add('ollo-track-key');
                diamond.style.left = `${offsetFromFrame(keyFrame)}px`;
                diamond.dataset.frame = keyFrame.toString();
                let dragging = false;
                let copying = false;
                let clone: HTMLElement | null = null;
                let toFrame = -1;
                diamond.addEventListener('pointerdown', (event: PointerEvent) => {
                    if (dragging || !event.isPrimary) return;
                    events.fire('track.setActive', targetOf(r.info));
                    dragging = true;
                    copying = event.shiftKey;
                    toFrame = -1;
                    diamond.setPointerCapture(event.pointerId);
                    event.stopPropagation();
                    if (copying) {
                        clone = document.createElement('div');
                        clone.classList.add('ollo-track-key', 'dragging');
                        clone.style.left = diamond.style.left;
                        r.strip.appendChild(clone);
                        diamond.classList.add('copying');
                    } else {
                        diamond.classList.add('dragging');
                    }
                });
                diamond.addEventListener('pointermove', (event: PointerEvent) => {
                    if (!dragging) return;
                    const rect = r.strip.getBoundingClientRect();
                    toFrame = frameFromOffset(event.clientX - rect.left);
                    (copying && clone ? clone : diamond).style.left = `${offsetFromFrame(toFrame)}px`;
                });
                diamond.addEventListener('pointerup', (event: PointerEvent) => {
                    if (!dragging || !event.isPrimary) return;
                    const fromFrame = parseInt(diamond.dataset.frame!, 10);
                    if (copying && clone) {
                        clone.remove();
                        clone = null;
                        diamond.classList.remove('copying');
                    } else {
                        diamond.classList.remove('dragging');
                    }
                    diamond.releasePointerCapture(event.pointerId);
                    if (toFrame >= 0 && toFrame !== fromFrame) {
                        events.fire(copying ? 'track.copyKey' : 'track.moveKey', fromFrame, toFrame);
                    } else {
                        // a plain click parks the playhead on the key
                        events.fire('timeline.setFrame', fromFrame);
                        buildKeys(r);
                    }
                    copying = false;
                    dragging = false;
                });
                r.strip.appendChild(diamond);
            }
        };

        const rebuildKeys = () => rows.forEach(buildKeys);

        const movePlayhead = (frame: number) => {
            const left = `${offsetFromFrame(frame)}px`;
            rows.forEach((r) => {
                r.playhead.style.left = left;
            });
        };

        const refreshActive = () => {
            rows.forEach((r) => {
                r.row.class[rowIsActive(r.info) ? 'add' : 'remove']('active');
            });
        };

        const buildRow = (info: TrackInfo): Row => {
            const row = new Container({ class: 'ollo-track-row' });
            const label = new Container({ class: 'ollo-track-label' });
            const name = new Label({ class: 'ollo-track-label-name', text: info.kind === 'camera' ? i18n.t('ollo.timeline.camera') : info.elementName });
            const effect = new Label({ class: 'ollo-track-label-effect', text: info.kind === 'camera' ? i18n.t('ollo.timeline.camera-pose') : info.effectName });
            label.append(name);
            label.append(effect);
            tooltips.register(label, () => `${name.text} · ${effect.text}`, 'right');
            label.dom.addEventListener('click', () => events.fire('track.setActive', targetOf(info)));
            if (info.kind === 'effect') {
                const remove = new Button({ class: 'ollo-track-remove', text: '' });
                tooltips.register(remove, () => i18n.t('ollo.timeline.remove-track'), 'right');
                remove.on('click', (event: MouseEvent) => {
                    event.stopPropagation();
                    events.fire('track.remove', targetOf(info));
                });
                label.append(remove);
            }
            const strip = document.createElement('div');
            strip.className = 'ollo-track-strip';
            const playhead = document.createElement('div');
            playhead.className = 'ollo-track-playhead';
            strip.appendChild(playhead);
            strip.addEventListener('pointerdown', (event: PointerEvent) => {
                if (event.target !== strip) return;
                events.fire('track.setActive', targetOf(info));
                events.fire('timeline.setFrame', frameFromOffset(event.offsetX));
            });
            row.append(label);
            row.dom.appendChild(strip);
            return { info, row, strip, playhead };
        };

        const rebuildList = () => {
            rows.forEach(r => r.row.destroy());
            rows = [];
            const infos = (events.invoke('tracks.discover') as TrackInfo[]) ?? [];
            for (const info of infos) {
                const r = buildRow(info);
                rows.push(r);
                rowsContainer.append(r.row);
            }
            rebuildKeys();
            refreshActive();
            movePlayhead(events.invoke('timeline.frame') as number);
            rebuildConfig();
        };

        // ── value of the active effect at the playhead ───────────────────
        // A key captures the effect's current value, so this is where an
        // effect gets anything other than its "no effect" identity. Editing
        // on a frame that already holds a key updates that key.
        let valueSync: (() => void) | null = null;
        let valueSyncQueued = false;

        // tracks evaluate on the same events this listens to, so read the
        // value a frame later, once they have applied it
        const queueValueSync = () => {
            if (valueSyncQueued || !valueSync) return;
            valueSyncQueued = true;
            requestAnimationFrame(() => {
                valueSyncQueued = false;
                valueSync?.();
            });
        };

        const buildValueItem = (def: EffectDef, element: Element, scene: Scene): Container => {
            const adapter = def.makeAdapter(element, scene);
            const item = new Container({ class: ['ollo-track-config-item', 'ollo-track-value'] });
            const label = new Label({ class: 'ollo-track-config-label', text: i18n.t('ollo.timeline.value') });
            tooltips.register(label, () => i18n.t('ollo.timeline.value.tooltip'), 'top');
            item.append(label);

            let syncing = false;
            const apply = (value: number[]) => {
                if (syncing) return;
                adapter.set(value);
                scene.forceRender = true;
                const frame = events.invoke('timeline.frame') as number;
                if (((events.invoke('track.keys') as number[]) ?? []).includes(frame)) {
                    events.fire('track.addKey', frame);
                }
            };

            if (def.kind === 'color') {
                const c = adapter.get();
                const picker = new ColorPicker({ class: 'ollo-track-value-color', value: [c[0], c[1], c[2], 1] });
                picker.on('change', (v: number[]) => apply([v[0], v[1], v[2]]));
                item.append(picker);
                valueSync = () => {
                    const v = adapter.get();
                    syncing = true;
                    picker.value = [v[0], v[1], v[2], 1];
                    syncing = false;
                };
                return item;
            }

            const dim = def.kind === 'vec3' ? 3 : 1;
            const [min, max] = def.kind === 'bool' ? [0, 1] : (def.range ?? [-Infinity, Infinity]);
            const span = Number.isFinite(min) && Number.isFinite(max) ? max - min : 10;
            const current = adapter.get();
            const inputs: NumericInput[] = [];
            for (let i = 0; i < dim; i++) {
                const input = new NumericInput({
                    class: 'ollo-track-config-input',
                    value: current[i] ?? 0,
                    min: Number.isFinite(min) ? min : undefined,
                    max: Number.isFinite(max) ? max : undefined,
                    step: def.kind === 'bool' || span > 20 ? 1 : span > 2 ? 0.1 : 0.01,
                    precision: def.kind === 'bool' ? 0 : span > 20 ? 1 : 3,
                    hideSlider: true
                });
                input.on('change', () => apply(inputs.map(inp => inp.value)));
                inputs.push(input);
                item.append(input);
            }
            valueSync = () => {
                const v = adapter.get();
                syncing = true;
                inputs.forEach((inp, i) => {
                    inp.value = v[i] ?? 0;
                });
                syncing = false;
            };
            return item;
        };

        // ── config knobs for the active effect ───────────────────────────
        const rebuildConfig = () => {
            configRow.clear();
            valueSync = null;
            const def = events.invoke('track.activeDef') as EffectDef | null;
            const t = activeTarget();
            const scene = events.functions.has('ollo.scene') ? (events.invoke('ollo.scene') as Scene) : null;
            if (!def || !t.element || !scene) {
                configRow.hidden = true;
                return;
            }
            configRow.hidden = false;
            const element = t.element;
            configRow.append(buildValueItem(def, element, scene));
            for (const param of def.config ?? []) {
                const item = new Container({ class: 'ollo-track-config-item' });
                const label = new Label({ class: 'ollo-track-config-label', text: param.label });
                if (param.description) tooltips.register(label, () => param.description!, 'top');
                item.append(label);
                const current = param.get(element, scene);
                const dim = param.kind === 'number' ? 1 : 3;
                const inputs: NumericInput[] = [];
                for (let i = 0; i < dim; i++) {
                    const [min, max] = param.range ?? [-Infinity, Infinity];
                    const span = Number.isFinite(min) && Number.isFinite(max) ? max - min : 10;
                    const input = new NumericInput({
                        class: 'ollo-track-config-input',
                        value: current[i] ?? 0,
                        min: Number.isFinite(min) ? min : undefined,
                        max: Number.isFinite(max) ? max : undefined,
                        step: span > 20 ? 1 : span > 2 ? 0.1 : 0.01,
                        precision: span > 20 ? 1 : 3,
                        hideSlider: true
                    });
                    input.on('change', () => {
                        param.set(element, scene, inputs.map(inp => inp.value));
                    });
                    inputs.push(input);
                    item.append(input);
                }
                configRow.append(item);
            }
        };

        // ── wiring ───────────────────────────────────────────────────────
        ['track.added', 'track.removed', 'scene.elementAdded', 'scene.elementRemoved', 'scene.clear', 'track.keysLoaded'].forEach((name) => {
            events.on(name, () => {
                refreshElementPicker();
                rebuildList();
            });
        });
        ['track.keyAdded', 'track.keyRemoved', 'track.keyMoved', 'track.keyUpdated', 'track.keysCleared', 'timeline.frames'].forEach((name) => {
            events.on(name, () => rebuildKeys());
        });
        events.on('track.activeChanged', () => {
            refreshActive();
            rebuildConfig();
        });
        events.on('timeline.frame', (frame: number) => {
            movePlayhead(frame);
            queueValueSync();
        });
        ['timeline.time', 'pivot.ended', 'track.keysLoaded'].forEach((name) => {
            events.on(name, () => queueValueSync());
        });
        new ResizeObserver(() => {
            rebuildKeys();
            movePlayhead(events.invoke('timeline.frame') as number);
        }).observe(rowsContainer.dom);
        i18n.onChange(() => {
            refreshElementPicker();
            rebuildList();
        }, this);

        refreshElementPicker();
        rebuildList();
    }
}

export { TrackList };
